import React, {useReducer, useRef} from 'react'
import Panel from '../../../../components/UI/surfaces/Panel.js'
import EditShippingRuleSet from '../../../../components/settings/shippingRules/EditShippingRuleSet.js'
import {Button, Card, H3, IconButton, ListItem, P, Popup, Row} from '../../../../components/UI/index.js'
import {Reseller} from '../../../../../server/functions/resellers/resellers.types.js'
import {ParcelCarrier} from '../../../../../server/functions/parcels/carrier.types.js'
import guid from '../../../../../utils/guid.js'
import naturalSortBy from '../../../../utils/naturalSortBy.js'
import t from '../../../../helpers/Languages.js'
import type {PopupRef} from '../../../../components/UI/overlays/Popup.js'

interface ShippingRulesProps {
    reseller: Reseller
    carriers: ParcelCarrier[]
    onChange: (reseller: Reseller, path: string) => void
}

interface ShippingRulesState {
    selectedShippingRuleSet: Reseller['settings']['parcels']['shippingRules'][0] | null
}

export default function ShippingRules({reseller, carriers, onChange}: ShippingRulesProps) {
    const [state, setState] = useReducer((state: ShippingRulesState, newState: Partial<ShippingRulesState>) => ({...state, ...newState}), {
        selectedShippingRuleSet: null
    })

    const popupRef = useRef<PopupRef>(null)

    const addShippingRuleSet = () => {
        const newShippingRuleSet: Reseller['settings']['parcels']['shippingRules'][0] = {
            id: guid.generate(),
            name: '',
            shippingRules: []
        }

        reseller.settings.parcels.shippingRules.push(newShippingRuleSet)
        onChange(reseller, 'parcels.shippingRules')

        setState({selectedShippingRuleSet: newShippingRuleSet})
    }

    const removeShippingRuleSet = (event: React.MouseEvent, shippingRuleSet: Reseller['settings']['parcels']['shippingRules'][0]) => {
        event.stopPropagation()

        popupRef.current?.open('Verzendregel set verwijderen', 'Weet je zeker dat je deze set wilt verwijderen?', () => {
            const index = reseller.settings.parcels.shippingRules.findIndex((ruleSet) => ruleSet.id === shippingRuleSet.id)

            if (index !== -1) {
                reseller.settings.parcels.shippingRules.splice(index, 1)
                onChange(reseller, 'parcels.shippingRules')
            }

            if (state.selectedShippingRuleSet?.id === shippingRuleSet.id) {
                setState({selectedShippingRuleSet: null})
            }

            popupRef.current?.close()
        })
    }

    return (
        <Row style={{gap: 24, justifyContent: 'flex-start'}}>
            <Card shadow style={{display: 'flex', flexDirection: 'column', padding: 24, width: 600, height: 'fit-content'}}>
                <H3 style={{marginBottom: 36}}>Verzendregels</H3>

                <P style={{marginBottom: 24}}>Verzendregels worden aangemaakt binnen een set. Vanuit de klantinstellingen kun je vervolgens kiezen welke set voor die klant toegepast moet worden.</P>

                {reseller.settings.parcels.shippingRules.length > 0 &&
                    <Panel style={{flex: 1, overflowY: 'auto', maxHeight: 500, marginBottom: 24}}>
                        {naturalSortBy(reseller.settings.parcels.shippingRules || [], 'name').map((shippingRuleSet) => (
                            <ListItem
                                key={shippingRuleSet.id}
                                active={state.selectedShippingRuleSet?.id === shippingRuleSet.id}
                                onClick={() => {
                                    setState({selectedShippingRuleSet: shippingRuleSet})
                                }}
                            >
                                {shippingRuleSet.name}

                                <div style={{flex: 1}} />

                                <IconButton
                                    style={{marginLeft: 15}}
                                    icon='mdi mdi-delete'
                                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => removeShippingRuleSet(event, shippingRuleSet)}
                                />
                            </ListItem>
                        ))}
                    </Panel>
                }

                <Button
                    variant='outline'
                    icon='mdi mdi-plus'
                    label={t('shippingRuleSet')}
                    onClick={addShippingRuleSet}
                />
            </Card>

            {state.selectedShippingRuleSet &&
                <EditShippingRuleSet
                    reseller={reseller}
                    carriers={carriers}
                    shippingRuleSet={state.selectedShippingRuleSet}
                    onSave={(shippingRuleSet) => {
                        const index = reseller.settings.parcels.shippingRules.findIndex((ruleSet) => ruleSet.id === shippingRuleSet.id)
                        reseller.settings.parcels.shippingRules[index] = shippingRuleSet
                        onChange(reseller, 'parcels.shippingRules')
                    }}
                    onCancel={() => setState({selectedShippingRuleSet: null})}
                />
            }

            <Popup ref={popupRef} />
        </Row>
    )
}
